import { jwtDecode } from 'jwt-decode';

import base64 from '@/helpers/base64';
import { Versao, VersionError, versoes } from '@/helpers/versionamento';
import JwtPayload from '@/models/JwtPayload';
import serverTimeService from '@/services/ServerTimeService';

// Lista de credores que devem receber o prefixo "mc-" nas APIs
const CREDORES_MIGRADOS = new Set(['inter', 'midway']);

export default class JwtAuthToken {
  rawToken: string;
  payload: JwtPayload;

  constructor(token: string) {
    this.rawToken = token;
    this.payload = jwtDecode(token);
  }

  static fromJSON(json: string): JwtAuthToken {
    const obj = JSON.parse(json);
    return new JwtAuthToken(obj.accessToken);
  }

  static toJSON(token: JwtAuthToken): string {
    return JSON.stringify({ accessToken: token.rawToken });
  }

  private static obterApis(apisString: string): string[] {
    return apisString.split('|').map((api) => {
      if (CREDORES_MIGRADOS.has(api)) {
        return `mc-${api}`;
      }
      return api;
    });
  }

  get apis(): string[] {
    return JwtAuthToken.obterApis(this.payload.apis);
  }

  get apisPrioritarias(): string[] {
    if (!this.payload.apisPrioritarias) {
      return [];
    }
    return JwtAuthToken.obterApis(this.payload.apisPrioritarias);
  }

  get parceiro(): string | undefined {
    const valor = this.payload.parceiro?.trim();
    if (!valor) {
      return undefined;
    }
    const api = JwtAuthToken.obterApis(valor)[0];
    return api || undefined;
  }

  get primeiroNome(): string {
    return base64.decode(this.payload.preferred_username);
  }

  get pendingPoliciesToAccept(): Versao[] {
    const validar: Record<Versao, string> = {
      termoUso: this.payload.versao_termo_uso,
      politicaPrivacidade: this.payload.versao_politica_privacidade,
    };

    const pending: Versao[] = [];
    (Object.entries(validar) as [Versao, string][])
      .forEach(([key, val]) => {
        try {
          if (!versoes[key].isEqual(val)) pending.push(key);
        } catch (e) {
          if (e instanceof VersionError) pending.push(key);
        }
      });

    return pending;
  }

  get timeLeftToExpire(): number {
    return (this.payload.exp * 1000) - serverTimeService.syncedTime.getTime();
  }

  get isExpired(): boolean {
    return this.timeLeftToExpire <= 0;
  }

  get isBackOffice(): boolean {
    const referrerWhitelist = new Set([
      'backofficedev.acerto.com.br',
      'backofficehmg.acerto.com.br',
      'backoffice.acerto.com.br',
    ]);

    const referrerHost = document.referrer
      ? new URL(document.referrer).hostname
      : '';

    return (this.payload.utmSource === 'backoffice')
    && referrerWhitelist.has(referrerHost);
  }
}
